import { traducirError } from "../../services/datos";
import { supabase } from "../../services/supabase";

// Proyectos en Supabase: tablas con reglas RLS (quién gestiona cada proyecto y tarea) y archivos en Storage
// (bucket privado "proyectos" y público "proyectos-portadas"). Devuelve los datos con la forma que ya usan
// las pantallas (lider, responsable, portada_url, contadores, autor…).

const DOCUMENTOS = "proyectos";
const PORTADAS = "proyectos-portadas";
export const MAX_DOCUMENTO_MB = 20;
const EXTENSION_PORTADA = { "image/jpeg": "jpg", "image/png": "png", "image/webp": "webp" };

function revisar({ data, error }) {
  if (error) throw traducirError(error);
  return data;
}

const aleatorio = () => (crypto.randomUUID ? crypto.randomUUID().replace(/-/g, "") : `${Date.now()}${Math.random()}`.replace(".", ""));

// Nombre para Storage, sin tildes ni caracteres especiales (el original se guarda aparte).
export function nombreSeguro(nombre) {
  const sinTildes = (nombre || "").normalize("NFD").replace(/[̀-ͯ]/g, "");
  return sinTildes.replace(/[^A-Za-z0-9._-]+/g, "_").slice(-80) || "archivo";
}

// ─── Personas (solo el nombre) ────────────────────────────────

let personasEnCurso = null;
export function personas() {
  if (!personasEnCurso) {
    personasEnCurso = supabase.rpc("proyectos_personas").then(revisar).then((d) => d || []);
    personasEnCurso.catch(() => {}).finally(() => setTimeout(() => { personasEnCurso = null; }, 30000));
  }
  return personasEnCurso;
}

async function mapaPersonas() {
  return new Map((await personas()).map((p) => [p.id, p.nombre]));
}

// Las pantallas muestran a la persona con nombrePersona({ nombres, apellidos }).
const persona = (mapa, id) => (id && mapa.has(id) ? { nombres: mapa.get(id), apellidos: "" } : null);

const urlPortada = (ruta) => (ruta ? supabase.storage.from(PORTADAS).getPublicUrl(ruta).data.publicUrl : null);

// ─── Proyectos ───────────────────────────────────────────────

function armarProyecto(p, mapa, tareas = []) {
  const propias = tareas.filter((t) => t.proyecto_id === p.id);
  return {
    ...p,
    lider: persona(mapa, p.lider_id),
    creado_por: mapa.get(p.creado_por_id) || null,
    portada_url: urlPortada(p.portada),
    tareas_total: propias.length,
    tareas_hechas: propias.filter((t) => t.estado === "completada").length,
  };
}

export async function listarProyectos() {
  const [lista, tareas, mapa] = await Promise.all([
    supabase.from("proyectos").select("*").order("creado_en", { ascending: false }).then(revisar),
    supabase.from("tareas_proyecto").select("proyecto_id, estado").then(revisar),
    mapaPersonas(),
  ]);
  return lista.map((p) => armarProyecto(p, mapa, tareas));
}

const CAMPOS_PROYECTO = ["nombre", "descripcion", "ministerio", "lider_id", "fecha_inicio", "fecha_fin", "estado", "prioridad"];
const CAMPOS_TAREA = ["proyecto_id", "titulo", "descripcion", "responsable_id", "fecha_inicio", "fecha_limite", "estado", "prioridad"];

function solo(datos, campos) {
  const limpio = {};
  for (const campo of campos) {
    if (!(campo in datos)) continue;
    const valor = typeof datos[campo] === "string" ? datos[campo].trim() : datos[campo];
    limpio[campo] = valor === "" ? null : valor;
  }
  return limpio;
}

export async function guardarProyecto(id, datos) {
  const filas = solo(datos, CAMPOS_PROYECTO);
  const consulta = id
    ? supabase.from("proyectos").update(filas).eq("id", id)
    : supabase.from("proyectos").insert(filas);
  const guardado = revisar(await consulta.select().single());
  return armarProyecto(guardado, await mapaPersonas());
}

// La portada se sube al bucket público con un nombre aleatorio; la anterior se borra.
export async function cambiarPortada(proyecto, imagen) {
  const tipo = imagen.type || "image/jpeg";
  const extension = EXTENSION_PORTADA[tipo];
  if (!extension) throw new Error("La portada debe ser una imagen JPG, PNG o WebP");
  const ruta = `${proyecto.id}/${aleatorio()}.${extension}`;
  revisar(await supabase.storage.from(PORTADAS).upload(ruta, imagen, { contentType: tipo }));
  let guardado;
  try {
    guardado = revisar(await supabase.from("proyectos").update({ portada: ruta }).eq("id", proyecto.id).select().single());
  } catch (error) {
    await supabase.storage.from(PORTADAS).remove([ruta]); // Sin el registro, la imagen quedaría suelta.
    throw error;
  }
  if (proyecto.portada) await supabase.storage.from(PORTADAS).remove([proyecto.portada]);
  return armarProyecto(guardado, await mapaPersonas());
}

export async function quitarPortada(proyecto) {
  const guardado = revisar(await supabase.from("proyectos").update({ portada: null }).eq("id", proyecto.id).select().single());
  if (proyecto.portada) await supabase.storage.from(PORTADAS).remove([proyecto.portada]);
  return armarProyecto(guardado, await mapaPersonas());
}

// Todos los archivos de una carpeta (también los que quedaron sin registro por una subida a medias).
async function archivosDeCarpeta(bucket, carpeta) {
  const { data } = await supabase.storage.from(bucket).list(carpeta, { limit: 1000 });
  return (data || []).filter((a) => a.id).map((a) => `${carpeta}/${a.name}`);
}

// Primero los archivos (después ya no se sabría de qué proyecto eran) y luego el proyecto, que se lleva
// en cascada sus tareas, comentarios y documentos. No queda nada suelto en Storage.
export async function eliminarProyecto(proyecto) {
  const tareas = revisar(await supabase.from("tareas_proyecto").select("id").eq("proyecto_id", proyecto.id));
  const carpetas = [`proyectos/${proyecto.id}`, ...tareas.map((t) => `tareas/${t.id}`)];
  const documentos = (await Promise.all(carpetas.map((c) => archivosDeCarpeta(DOCUMENTOS, c)))).flat();
  if (documentos.length) revisar(await supabase.storage.from(DOCUMENTOS).remove(documentos));
  const portadas = await archivosDeCarpeta(PORTADAS, String(proyecto.id));
  if (portadas.length) revisar(await supabase.storage.from(PORTADAS).remove(portadas));
  revisar(await supabase.from("proyectos").delete().eq("id", proyecto.id));
}

// ─── Tareas ──────────────────────────────────────────────────

// Con cuántos comentarios y documentos tiene cada una (se muestran en las tarjetas).
export async function listarTareas() {
  const [lista, mapa] = await Promise.all([
    supabase.from("tareas_proyecto").select("*, comentarios_tarea(count), adjuntos_proyecto(count)")
      .order("fecha_limite", { ascending: true, nullsFirst: false }).then(revisar),
    mapaPersonas(),
  ]);
  return lista.map(({ comentarios_tarea: comentarios, adjuntos_proyecto: adjuntos, ...t }) => ({
    ...t,
    responsable: persona(mapa, t.responsable_id),
    creado_por: mapa.get(t.creado_por_id) || null,
    comentarios: comentarios?.[0]?.count || 0,
    adjuntos: adjuntos?.[0]?.count || 0,
  }));
}

// El responsable solo puede cambiar el estado: la base rechaza cualquier otro cambio suyo.
export async function guardarTarea(id, datos) {
  const filas = solo(datos, CAMPOS_TAREA);
  const consulta = id
    ? supabase.from("tareas_proyecto").update(filas).eq("id", id)
    : supabase.from("tareas_proyecto").insert(filas);
  const guardada = revisar(await consulta.select().single());
  const mapa = await mapaPersonas();
  return { ...guardada, responsable: persona(mapa, guardada.responsable_id), creado_por: mapa.get(guardada.creado_por_id) || null };
}

export async function eliminarTarea(tarea) {
  const documentos = await archivosDeCarpeta(DOCUMENTOS, `tareas/${tarea.id}`);
  if (documentos.length) revisar(await supabase.storage.from(DOCUMENTOS).remove(documentos));
  revisar(await supabase.from("tareas_proyecto").delete().eq("id", tarea.id));
}

// ─── Comentarios ─────────────────────────────────────────────

export async function listarComentarios(tareaId) {
  const [lista, mapa] = await Promise.all([
    supabase.from("comentarios_tarea").select("*").eq("tarea_id", tareaId).order("creado_en").then(revisar),
    mapaPersonas(),
  ]);
  return lista.map((c) => ({ ...c, autor: mapa.get(c.autor_id) || "Usuario" }));
}

// El autor lo pone la base: es quien tiene la sesión.
export async function comentar(tareaId, texto) {
  revisar(await supabase.from("comentarios_tarea").insert({ tarea_id: tareaId, texto }));
}

export async function eliminarComentario(id) {
  revisar(await supabase.from("comentarios_tarea").delete().eq("id", id));
}

// ─── Documentos ──────────────────────────────────────────────

// De una tarea, o de un proyecto incluidos los de todas sus tareas. Las imágenes traen "miniatura":
// un enlace temporal para verlas en la lista.
export async function listarDocumentos(destino) {
  let consulta = supabase.from("adjuntos_proyecto").select("*").order("creado_en", { ascending: false });
  if (destino.tarea_id) {
    consulta = consulta.eq("tarea_id", destino.tarea_id);
  } else {
    const tareas = revisar(await supabase.from("tareas_proyecto").select("id").eq("proyecto_id", destino.proyecto_id));
    const ids = tareas.map((t) => t.id);
    consulta = ids.length
      ? consulta.or(`proyecto_id.eq.${destino.proyecto_id},tarea_id.in.(${ids.join(",")})`)
      : consulta.eq("proyecto_id", destino.proyecto_id);
  }
  const [lista, mapa] = await Promise.all([consulta.then(revisar), mapaPersonas()]);
  const imagenes = lista.filter((a) => String(a.tipo || "").startsWith("image/")).map((a) => a.ruta);
  const miniaturas = new Map();
  if (imagenes.length) {
    const { data } = await supabase.storage.from(DOCUMENTOS).createSignedUrls(imagenes, 3600);
    (data || []).forEach((e) => e.signedUrl && miniaturas.set(e.path, e.signedUrl));
  }
  return lista.map((a) => ({ ...a, subido_por: mapa.get(a.subido_por_id) || null, miniatura: miniaturas.get(a.ruta) || null }));
}

export async function subirDocumento(destino, archivo) {
  if (archivo.size > MAX_DOCUMENTO_MB * 1024 * 1024) throw new Error(`${archivo.name} supera los ${MAX_DOCUMENTO_MB} MB.`);
  if (!archivo.size) throw new Error(`${archivo.name} está vacío.`);
  const carpeta = destino.tarea_id ? `tareas/${destino.tarea_id}` : `proyectos/${destino.proyecto_id}`;
  const ruta = `${carpeta}/${aleatorio()}-${nombreSeguro(archivo.name)}`;
  const tipo = archivo.type || "application/octet-stream";
  revisar(await supabase.storage.from(DOCUMENTOS).upload(ruta, archivo, { contentType: tipo }));
  try {
    revisar(await supabase.from("adjuntos_proyecto").insert({
      proyecto_id: destino.tarea_id ? null : destino.proyecto_id,
      tarea_id: destino.tarea_id || null,
      nombre: archivo.name.slice(0, 255),
      ruta,
      tamano: archivo.size,
      tipo,
    }));
  } catch (error) {
    await supabase.storage.from(DOCUMENTOS).remove([ruta]); // Sin el registro, el archivo quedaría suelto.
    throw error;
  }
}

// Enlace temporal (1 minuto) que descarga el archivo con su nombre original.
export async function enlaceDescarga(adjunto) {
  const datos = revisar(await supabase.storage.from(DOCUMENTOS).createSignedUrl(adjunto.ruta, 60, { download: adjunto.nombre }));
  return datos.signedUrl;
}

// Primero el registro (si las reglas no lo permiten, no se borra nada) y luego el archivo.
export async function eliminarDocumento(adjunto) {
  const borrados = revisar(await supabase.from("adjuntos_proyecto").delete().eq("id", adjunto.id).select("id"));
  if (!borrados.length) throw new Error("No tienes permiso para eliminar este documento");
  await supabase.storage.from(DOCUMENTOS).remove([adjunto.ruta]);
}
